import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  type InfiniteData,
} from "@tanstack/react-query";

import {
  membersListResponseSchema,
  type MemberLifecycleStage,
  type MembersListResponse,
} from "@community-analytics/shared";

import { apiFetch } from "@shared/api";

import { memberQueryKeys } from "./keys";

/**
 * GET /api/communities/:id/members — TASK-031 wire contract, paginated via
 * the cursor returned by the previous page. The backend chose cursor-based
 * pagination (lastActiveAt DESC NULLS LAST, id DESC) because lurkers with a
 * null lastActiveAt would break offset pagination across joins/leaves.
 *
 * The hook is a no-op when `communityId` is null (e.g. before the
 * communities list resolves and the active-community store picks one) —
 * `enabled: false` keeps TanStack Query from firing a request with a
 * malformed URL.
 *
 * `stages` is treated as part of the cache key (see keys.ts). When the
 * caller toggles a chip we want a fresh first page, not a re-render of a
 * page that was filtered differently. The backend rejects an empty
 * `?stage=` so we omit the param when no chips are selected (= "all
 * stages").
 */
export function useMembersQuery(
  communityId: string | null,
  stages: ReadonlyArray<MemberLifecycleStage>,
): UseInfiniteQueryResult<InfiniteData<MembersListResponse, string | null>, Error> {
  return useInfiniteQuery<
    MembersListResponse,
    Error,
    InfiniteData<MembersListResponse, string | null>,
    ReturnType<typeof memberQueryKeys.list>,
    string | null
  >({
    queryKey: memberQueryKeys.list(communityId ?? "__noop__", stages),
    enabled: communityId !== null,
    initialPageParam: null,
    queryFn: async ({ pageParam }) => {
      if (communityId === null) {
        // Unreachable in production — `enabled` gates the call. Re-asserted
        // for the type narrower so we don't have to non-null-assert below.
        throw new Error("communityId is required");
      }
      const params = new URLSearchParams();
      if (stages.length > 0) {
        params.set("stage", [...stages].sort().join(","));
      }
      if (pageParam) {
        params.set("cursor", pageParam);
      }
      const qs = params.toString();
      const url = `/api/communities/${encodeURIComponent(communityId)}/members${
        qs ? `?${qs}` : ""
      }`;
      const raw = await apiFetch<unknown>(url);
      return membersListResponseSchema.parse(raw);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}
